import React from 'react';
import { Button, Select, Tooltip, Slider, Switch, Typography } from 'antd';
import {
  PlayCircleFilled,
  PauseCircleFilled,
  StepBackwardFilled,
  StepForwardFilled,
  SoundFilled,
  CustomerServiceFilled,
  ThunderboltFilled,
  AudioMutedOutlined,
  SubnodeOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

interface SubtitleConfig {
  show: boolean;
  onToggle: (v: boolean) => void;
  lang: string;
  onLangChange: (lang: string) => void;
  languages: { value: string; label: string }[];
}

interface EnhancedPlayerControlsProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  onPrevPanel: () => void;
  onNextPanel: () => void;
  currentTime: number;
  totalDuration: number;
  onSeek: (time: number) => void;

  mutedTracks: {
    voice: boolean;
    bgm: boolean;
    sfx: boolean;
  };
  onToggleMute: (track: 'voice' | 'bgm' | 'sfx') => void;

  playbackSpeed: number;
  onSpeedChange: (speed: number) => void;

  subtitle?: SubtitleConfig;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
};

export const EnhancedPlayerControls: React.FC<EnhancedPlayerControlsProps> = ({
  isPlaying,
  onPlayPause,
  onPrevPanel,
  onNextPanel,
  currentTime,
  totalDuration,
  onSeek,
  mutedTracks,
  onToggleMute,
  playbackSpeed,
  onSpeedChange,
  subtitle,
}) => {
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* ── Progress bar ── */}
      <div style={{ padding: '4px 12px 0' }}>
        <Slider
          value={progress}
          min={0}
          max={100}
          step={0.1}
          tooltip={{ formatter: (v) => formatTime(((v ?? 0) / 100) * totalDuration) }}
          onChange={(v) => onSeek((v / 100) * totalDuration)}
          styles={{
            track: { backgroundColor: '#1677ff', height: 4 },
            rail: { backgroundColor: '#444', height: 4 },
            handle: { width: 12, height: 12, borderColor: '#1677ff', boxShadow: 'none' },
          }}
          style={{ margin: 0, height: 12 }}
        />
      </div>

      {/* ── Main controls row ── */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        padding: '4px 16px 8px',
      }}>
        {/* Left: nav + play */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
          <Tooltip title="Panel trước (Shift + ←)">
            <Button
              type="text"
              icon={<StepBackwardFilled style={{ fontSize: 16, color: '#ccc' }} />}
              onClick={onPrevPanel}
              style={{ width: 28, height: 28, padding: 0 }}
            />
          </Tooltip>

          <Tooltip title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}>
            <button
              onClick={onPlayPause}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1677ff',
                transition: 'background-color 0.2s',
                flexShrink: 0,
              }}
            >
              {isPlaying
                ? <PauseCircleFilled style={{ fontSize: 20, color: '#fff' }} />
                : <PlayCircleFilled style={{ fontSize: 20, color: '#fff' }} />}
            </button>
          </Tooltip>

          <Tooltip title="Panel tiếp theo (Shift + →)">
            <Button
              type="text"
              icon={<StepForwardFilled style={{ fontSize: 16, color: '#ccc' }} />}
              onClick={onNextPanel}
              style={{ width: 28, height: 28, padding: 0 }}
            />
          </Tooltip>
        </div>

        {/* Center: time — absolute để luôn căn giữa container */}
        <div style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 2,
          pointerEvents: 'none',
        }}>
          <Text style={{ fontSize: 13, color: '#fff', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {formatTime(currentTime)}
          </Text>
          <Text style={{ fontSize: 11, color: '#666' }}> / </Text>
          <Text style={{ fontSize: 11, color: '#888', fontVariantNumeric: 'tabular-nums' }}>
            {formatTime(totalDuration)}
          </Text>
        </div>

        {/* Right: audio tracks + speed + subtitles — push to right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', zIndex: 1 }}>
          {/* Audio track toggles */}
          <Tooltip title={mutedTracks.voice ? 'Voice: tắt' : 'Voice: bật'}>
            <Button
              type="text"
              onClick={() => onToggleMute('voice')}
              icon={mutedTracks.voice
                ? <AudioMutedOutlined style={{ fontSize: 14, color: '#555' }} />
                : <SoundFilled style={{ fontSize: 14, color: '#1677ff' }} />}
              style={{ width: 28, height: 28, padding: 0 }}
            />
          </Tooltip>

          <Tooltip title={mutedTracks.bgm ? 'BGM: tắt' : 'BGM: bật'}>
            <Button
              type="text"
              onClick={() => onToggleMute('bgm')}
              icon={<CustomerServiceFilled style={{ fontSize: 14, color: mutedTracks.bgm ? '#555' : '#52c41a' }} />}
              style={{ width: 28, height: 28, padding: 0 }}
            />
          </Tooltip>

          <Tooltip title={mutedTracks.sfx ? 'SFX: tắt' : 'SFX: bật'}>
            <Button
              type="text"
              onClick={() => onToggleMute('sfx')}
              icon={<ThunderboltFilled style={{ fontSize: 14, color: mutedTracks.sfx ? '#555' : '#faad14' }} />}
              style={{ width: 28, height: 28, padding: 0 }}
            />
          </Tooltip>

          <div style={{ width: 1, height: 16, backgroundColor: '#444', margin: '0 2px' }} />

          {/* Speed */}
          <Select
            value={playbackSpeed}
            onChange={onSpeedChange}
            style={{ width: 62 }}
            size="small"
            popupMatchSelectWidth={false}
          >
            <Option value={0.5}>0.5x</Option>
            <Option value={0.75}>0.75x</Option>
            <Option value={1}>1x</Option>
            <Option value={1.25}>1.25x</Option>
            <Option value={1.5}>1.5x</Option>
            <Option value={2}>2x</Option>
          </Select>

          {/* Subtitle toggle (nếu có) */}
          {subtitle && subtitle.languages.length > 0 && (
            <>
              <div style={{ width: 1, height: 16, backgroundColor: '#444', margin: '0 2px' }} />
              <Tooltip title="Phụ đề">
                <Button
                  type="text"
                  onClick={() => subtitle.onToggle(!subtitle.show)}
                  icon={<SubnodeOutlined style={{ fontSize: 14, color: subtitle.show ? '#1677ff' : '#555' }} />}
                  style={{ width: 28, height: 28, padding: 0 }}
                />
              </Tooltip>
              {subtitle.show && (
                <Select
                  value={subtitle.lang}
                  onChange={subtitle.onLangChange}
                  size="small"
                  style={{ width: 120 }}
                  popupMatchSelectWidth={false}
                  options={subtitle.languages}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
